import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Button, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, Typography } from '@mui/material';
import { Delete, DeleteOutlineOutlined, EditOutlined, ExpandMore, MoreVert } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { fetchAllSurveys } from '../../../../../app/store/surveySlice';
import { useCanAddNewWorkflow } from '../../../../../hooks/useCanAddNewWorkflow';
import { WorkflowLabels, wfHierarchy } from '../../../../../services/workflow';
import {
  Application,
  ApplicationType,
  Badge,
  Company,
  CompanyApplication,
  Version,
} from '../../../../../interfaces/users.interface';
import { patchSurvey } from '../../../../../services/surveys';
import {
  getHighestAchievement,
  fetchGoldBadgeLabel,
  fetchBlueBadgeLabel,
  CERTIFICATION_BADGE_LABEL,
  BLUE_BADGE_LABEL,
  GOLD_BADGE_LABEL,
  SILVER_BADGE_LABEL,
  GOLD_NEXT_GEN_BADGE_LABEL,
  GOLD_STAR_BADGE_LABEL,
  selectBadgeFromArray,
} from '../../../../../services/badge';
import { deleteCompanyApplication } from '../../../../../services/companyApplication';
import { createBadge, updateVersionBadge } from '../../../../../services/applications';
import GetBadge from '../../../GetBadge';
import NewCustomApp from '../../ApplicationModals/NewCustomApp';
import { DataCell, DataRow } from '../../../../Tables/DataList';
import ChipsWithOverflow from '../../../../Layout/ChipsWithOverflow';
import VersionDataRowV2 from './VersionsAccordion/VersionDataRowV2';
import VersionsAccordion from './VersionsAccordion/VersionsAccordion';
import StandardDialog, { StandardDialogActions } from '../../../../Modals/StandardDialog';
import { WorkflowTypeKey } from '../../../../../interfaces/workflow.interface';

type InhouseAppCardProps = {
  company: Company;
  companyApplication: CompanyApplication;
  userCanAdminCompany: boolean;
  companySuccessCallback: any;
  onVersionRowClick: (companyApp: CompanyApplication, app: Application, version: Version) => void;
};

const InhouseAppCard = (props: InhouseAppCardProps) => {
  const { company, companyApplication, userCanAdminCompany, companySuccessCallback, onVersionRowClick } = props;
  const dispatch = useAppDispatch();
  const { surveys } = useAppSelector((state) => state.surveys);
  const { workflows: companyWorkflows } = useAppSelector((state) => state.workflow);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cApplication, setcApplication] = useState(companyApplication);
  const [versionExpanded, setVersionExpanded] = useState(false);
  const [editApplicationOpen, setEditApplicationOpen] = useState(false);
  const [deleteApplicationOpen, setDeleteApplicationOpen] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const noVerSurveys = useMemo(
    () =>
      surveys?.filter(
        (cursur) =>
          (cursur.company as Company).id === props.company.id &&
          cApplication.application.id === cursur.application &&
          !cursur.version &&
          cursur.questionnaire,
      ),
    [surveys],
  );
  // Certs are embedded on the app by the apps endpoint (app- and version-matched);
  // pick the app-level one (its `applications` includes this app) for the badge.
  const companyCert = useMemo(
    () =>
      cApplication.certifications?.find((cert) =>
        cert.applications?.find((app) => app.id === cApplication.application.id),
      ),
    [cApplication.certifications, cApplication.application.id],
  );
  const siteNames = useMemo(() => cApplication?.sites?.map((site) => site.name), [cApplication?.sites]);
  const serviceNames = useMemo(() => cApplication?.services?.map((service) => service.name), [cApplication?.services]);
  const applicationTypes = useMemo(
    () => (cApplication?.application?.applicationTypes as ApplicationType[])?.map((app) => app?.type),
    [cApplication?.application?.applicationTypes],
  );
  const sortedVersionsList = useMemo(
    () => ([...cApplication.application.versions as Version[]])?.sort((a, b) => a?.value && b?.value ? a.value.localeCompare(b.value) : 0),
    [cApplication.application.versions],
  );
  const chipsCellStyle = useMemo(
    () => ({ display: 'flex', flexWrap: 'wrap', columnGap: '0.25rem', padding: '0.25rem' }),
    [],
  );
  const appWorkflows = useMemo(() => {
    if (!companyWorkflows) return [];
    return companyWorkflows
      .filter((w) => (w.application as Application)?.id === cApplication.application.id)
      .sort((a, b) =>
        wfHierarchy.indexOf(b.status as typeof wfHierarchy[number]) -
        wfHierarchy.indexOf(a.status as typeof wfHierarchy[number]),
      )
      .map((w) => ({ ...w }));
  }, [companyWorkflows, cApplication]);

  const highestAchievementBlue = useMemo(() => getHighestAchievement(appWorkflows, [BLUE_BADGE_LABEL], true), [appWorkflows]);
  const highestAchievementGold = useMemo(() => getHighestAchievement(
    appWorkflows,
    [GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL], true), [appWorkflows]);

  const blueBadgeLabel = useMemo(() => {
    if (!highestAchievementBlue?.badge) return '';
    return fetchBlueBadgeLabel([highestAchievementBlue.badge], highestAchievementBlue.version);
  }, [highestAchievementBlue]);

  const handleMenuClose = useCallback(() => setAnchorEl(null), []);
  const handleEditApplicationClose = useCallback(() => setEditApplicationOpen(false), []);
  const handleDeleteApplicationClose = useCallback(() => setDeleteApplicationOpen(false), []);
  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget), []);

  const handleDeleteApplicationOpen = useCallback(() => {
    handleMenuClose();
    setDeleteApplicationOpen(true);
  }, []);

  const handleEditApplicationOpen = useCallback(() => {
    handleMenuClose();
    setEditApplicationOpen(true);
  }, []);

  // v2 rows render from the server-paginated apps endpoint, so changes update
  // the local row and notify the parent (which re-fetches the page) rather than
  // rebuilding the company's full application list locally.
  const handleEditApplicationSubmit = useCallback(
    (companyApplicationUpdated: CompanyApplication) => {
      setcApplication(companyApplicationUpdated);
      companySuccessCallback(company);
      handleEditApplicationClose();
    },
    [company, companySuccessCallback, handleEditApplicationClose],
  );

  const handleDeleteApplicationSubmit = useCallback(async () => {
    await deleteCompanyApplication(cApplication);
    companySuccessCallback(company);
    handleDeleteApplicationClose();
  }, [cApplication, company, companySuccessCallback, handleDeleteApplicationClose]);

  const updateSurveys = useCallback(
    async (version: string) => {
      if (!noVerSurveys || !noVerSurveys.length) return;

      if (
        companyApplication.badge &&
        [CERTIFICATION_BADGE_LABEL, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL].indexOf(companyApplication.badge) !== -1
      ) {
        const date =
          companyApplication.goldShieldDate || companyApplication.blueShieldDate || moment().format('YYYY-MM-DD');
        const newBadge = await createBadge({
          title: companyApplication.badge as 'non_tpn_self_reported' | 'tpn_self_reported' | 'tpn_assessed',
          awardedDate: date,
          expirationDate: moment(date).add(2, 'years').format('YYYY-MM-DD'),
        });

        const appVersion = (companyApplication.application.versions as Version[])?.find((v) => v.value === version);
        if (!appVersion) return;

        const newVersion = await updateVersionBadge(appVersion.id!, newBadge.id!);
        const newVersions = [...(companyApplication.application.versions as Version[])!];
        const avIndex = newVersions.findIndex((nav) => nav.id === newVersion.id);
        if (avIndex > -1) {
          newVersions[avIndex] = newVersion;
          const companyApplicationUpdated = {
            ...cApplication,
            application: { ...cApplication.application, versions: newVersions },
          };
          setcApplication(companyApplicationUpdated);
          companySuccessCallback(company);
        }
      }

      await Promise.all(noVerSurveys.map((sur) => patchSurvey(sur.id, version)));
      dispatch(fetchAllSurveys({ company: company.id }));
    },
    [noVerSurveys, companyApplication, cApplication, company, dispatch],
  );

  const canAddNewWorkflow = useCanAddNewWorkflow(appWorkflows);
  let nextWorkflowLabel = WorkflowLabels.slice(-1)[0] as WorkflowTypeKey;

  if (appWorkflows.length > 0) {
    const currWorkflowIndex = WorkflowLabels.indexOf(appWorkflows[0]?.workflowType as WorkflowTypeKey);
    nextWorkflowLabel = WorkflowLabels[currWorkflowIndex + 1] as WorkflowTypeKey;
  }

  const canBeginNextQuestionnaire = useMemo(() => nextWorkflowLabel && appWorkflows.length > 0 && canAddNewWorkflow(nextWorkflowLabel), [nextWorkflowLabel, canAddNewWorkflow]);

  useEffect(() => {
    setcApplication(companyApplication);
  }, [companyApplication]);

  const allWorkflowsBadges = useMemo(() => appWorkflows.map((wf) => wf.badges).flat(), [appWorkflows]);

  return (
    <>
      {/* V1 accordion-body layout: an expandable row per app, versions in a
          collapsible sub-table. Data still comes from the server-paginated apps
          endpoint (embedded certs; refetch on mutate) rather than the full company. */}
      <DataRow
        onClick={() => { if (cApplication.application.versions?.length) setVersionExpanded(!versionExpanded); }}
        hover
        sx={{
          cursor: cApplication.application.versions?.length ? 'pointer' : 'default',
          position: 'relative',
          '&:hover': {
            '& .slide-icon': { color: 'common.white' },
            '& .hoverBox': { backgroundColor: 'primary.main' },
          },
        }}
      >
        <DataCell xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          {cApplication.application.versions?.length ? (
            <IconButton
              size='small'
              className='hoverBox'
              onClick={(e) => { e.stopPropagation(); setVersionExpanded(!versionExpanded); }}
              sx={(theme) => ({ transition: theme.transitions.create(['background-color', 'transform'], { duration: 300 }) })}
            >
              <ExpandMore className='slide-icon' sx={{ transform: versionExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </IconButton>
          ) : null}
        </DataCell>
        <DataCell xs={1} textAlign='center'>
          <GetBadge
            shield={allWorkflowsBadges && selectBadgeFromArray(allWorkflowsBadges as Badge[], true)}
            isActive={company.isPublished}
            certification={companyCert}
            goldBadgeLabel={highestAchievementGold?.badge && fetchGoldBadgeLabel([highestAchievementGold.badge], highestAchievementGold.version, highestAchievementGold.badge.title)}
            blueBadgeLabel={blueBadgeLabel}
            company={company.id}
          />
        </DataCell>
        <DataCell xs={3}>
          <Typography sx={{ fontSize: '1.2em' }}>{cApplication.application.name}</Typography>
        </DataCell>
        <DataCell xs={2} sx={chipsCellStyle}>
          {ChipsWithOverflow(siteNames, 1, 'site')}
        </DataCell>
        <DataCell xs={2} sx={chipsCellStyle}>
          {ChipsWithOverflow(serviceNames, 1, 'service')}
        </DataCell>
        <DataCell xs={2} sx={chipsCellStyle}>
          {ChipsWithOverflow(applicationTypes, 1, 'appType')}
        </DataCell>
        <DataCell xs={1} textAlign='center'>
          {userCanAdminCompany && (
            <>
              <IconButton onClick={(e) => { e.stopPropagation(); handleMenuClick(e); }}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                sx={{ '& .MuiMenu-paper': { minWidth: 175, color: '#808080' } }}
              >
                <MenuItem onClick={handleEditApplicationOpen} sx={{ columnGap: 0.625, alignItems: 'center' }}>
                  <EditOutlined />
                  <Typography>Edit</Typography>
                </MenuItem>
                <MenuItem onClick={handleDeleteApplicationOpen} sx={{ columnGap: 0.625, alignItems: 'center' }}>
                  <DeleteOutlineOutlined />
                  <Typography>Delete</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </DataCell>
        <DataCell xs={12} sx={{ ml: '4%' }}>
          {noVerSurveys && !!noVerSurveys.length && !!cApplication.application.versions?.length && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Typography>
                You have questionnaires for this application with no version, what version/apps would you like to
                associate with that questionnaire?
              </Typography>
              <FormControl fullWidth sx={{ maxWidth: '250px' }}>
                <InputLabel id={`version-label-${cApplication.id}`}>Version or Application</InputLabel>
                <Select
                  label='Version or Application'
                  labelId={`version-label-${cApplication.id}`}
                  onChange={(e) => e.target.value && updateSurveys(e.target.value as string)}
                >
                  {sortedVersionsList.map((av) => (
                    <MenuItem key={av.id} value={av.value}>
                      {av.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DataCell>
      </DataRow>
      <VersionsAccordion
        isExpanded={versionExpanded}
        company={company}
        companyApp={cApplication}
        application={cApplication.application}
        versions={sortedVersionsList}
        onVersionRowClick={onVersionRowClick}
        canBeginNextQuestionnaire={canBeginNextQuestionnaire}
        rowComponent={VersionDataRowV2}
      />

      {/* Dialogs */}
      <StandardDialog
        isOpen={deleteApplicationOpen}
        title={`Delete ${cApplication.application.name} ?`}
        handleClose={handleDeleteApplicationClose}
      >
        <Typography>Are you sure you want to delete {cApplication.application.name}?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleDeleteApplicationClose}>Cancel</Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<Delete />}
            disabled={!userCanAdminCompany}
            onClick={handleDeleteApplicationSubmit}
          >
            Delete
          </Button>
        </StandardDialogActions>
      </StandardDialog>
      <NewCustomApp
        isOpen={editApplicationOpen}
        company={company}
        companyApplication={cApplication}
        callBack={handleEditApplicationSubmit}
        handleClose={handleEditApplicationClose}
      />
    </>
  );
};

export default InhouseAppCard;
